import { useQuery } from "@tanstack/react-query";

export interface KeyDetail {
  label: string;
  value: string;
  icon: string;
}

export interface TechStackCategory {
  label: string;
  technologies: string[];
}

export interface SocialLink {
  name: string;
  url: string;
  icon: string;
}

export interface Skill {
  name: string;
  level: number;
}

export interface SkillCategory {
  name: string;
  icon: string;
  description: string;
  skills: Skill[];
}

export interface Project {
  id: number;
  title: string;
  category: string;
  description: string;
  image: string;
  technologies: string[];
  architecture?: string[];
  highlights?: string[];
  problem?: string;
  solution?: string;
  keyFeatures?: string[];
  screenshots?: string[];
  codeSample?: {
    method: string;
    endpoint: string;
    body: string;
  };
  role?: string;
  techStackCategories?: TechStackCategory[];
  isNDA?: boolean;
  demoUrl: string | null;
  repoUrl: string;
  isPrivate?: boolean;
  challenges?: string;
  metrics?: string[];
  decisions?: Array<{ question: string; answer: string }>;
  tradeoffs?: Array<{ decision: string; optionA: string; optionB: string; reason: string }>;
  lessonsLearned?: string;
}

export interface JobExperience {
  title: string;
  company: string;
  period: string;
  description: string;
  achievements: string[];
}

export interface Education {
  degree: string;
  institution: string;
  period: string;
  details?: string;
}

export interface Certification {
  name: string;
  issuer: string;
  date: string;
  icon: string;
  url: string;
}

export interface Contact {
  email: string;
  phone: string;
  location: string;
}

export interface PortfolioData {
  name: string;
  title: string;
  tagline: string;
  profileImage: string;
  resumeUrl: string;
  footerTagline: string;
  location: string;
  email: string;
  phone: string;
  linkedin: string;
  github: string;
  website: string;
  twitter?: string;
  bio: string;
  keyDetails: Array<{
    label: string;
    value: string;
    icon: string;
  }>;
  socialLinks: SocialLink[];
  skillCategories: SkillCategory[];
  allSkills: string[];
  featuredTech: Array<{
    name: string;
    icon: string;
  }>;
  githubStats: {
    repositories: string;
    commits: string;
    languages: string[];
    topProjects: Array<{
      name: string;
      url: string;
    }>;
  };
  projects: Project[];
  experience: JobExperience[];
  education: Education[];
  certifications: Certification[];
  contact: Contact;
}

import portfolioData from '@/data/portfolio.json';

export function usePortfolioData() {
  console.log('Importing portfolio data:', portfolioData);

  const result = useQuery<PortfolioData>({
    queryKey: ['portfolio'],
    queryFn: () => {
      console.log('Fetching portfolio data');
      return (portfolioData as unknown) as PortfolioData;
    },
    staleTime: Infinity, // Static data, never stale
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  console.log('Query result:', result);
  return result;
}
